import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/** item, value, where it is enforced, can it be changed */
type Row = [string, string, string, string];

type Area = { title: string; intro?: string; rows: Row[] };

const REST_429 = `HTTP/1.1 429 Too Many Requests
Retry-After: 1

{
  "statusCode": 429,
  "message": "Rate limit exceeded (second window)",
  "retryAfterSeconds": 1
}`;

const GQL_429 = `HTTP/1.1 429 Too Many Requests
Retry-After: 1

{ "errors": [ {
  "message": "Rate limit exceeded (second window)",
  "extensions": { "code": "RATE_LIMITED" }
} ] }`;

const KEY = 'Per key, by a tenant admin in Settings, API Keys';
const PLATFORM = 'Platform configuration, not a workspace setting';
const FIXED = 'Fixed';

const AREAS: Area[] = [
  {
    title: 'API keys and request rates',
    intro:
      'A key is counted in fixed windows: one clock second and one clock minute. Either window can refuse a request, and the message names which one.',
    rows: [
      ['Ordinary key, per second', '10', 'Every request made with a key: REST, public GraphQL, MCP', KEY],
      ['Ordinary key, per minute', '100', 'Same', KEY],
      ['Support sign-in key, per second', '50', 'Keys that carry the support sign-in capability', KEY],
      ['Support sign-in key, per minute', '3,000', 'Same', KEY],
      ['Highest limit a key can be given', '500 per second, 30,000 per minute', 'When you save a key’s limits. Whole numbers of 1 or more, and the minute limit cannot be below the second limit', FIXED],
      ['MCP with sign-in instead of a key', '10 per second, 300 per minute per person', 'The MCP endpoint', PLATFORM],
      ['Dashboard and other requests', '300 per minute per network address and signed-in user', 'The shared request limiter', PLATFORM],
      ['Sign-in attempts', '10 per minute per address, then blocked for 15 minutes', 'The sign-in endpoints', PLATFORM],
      ['Public GraphQL query depth', '8 levels, refused with QUERY_DEPTH_EXCEEDED', 'Before the query runs', FIXED],
      ['Public GraphQL invoices on a client', 'Default 100, at most 1,000 per call', 'The invoices field', 'Per request, through the limit argument'],
    ],
  },
  {
    title: 'Product events',
    intro:
      'These apply to usage events sent to /api/v1/product-events, from your servers or, with a token, from a browser. See Send product events for the request shape.',
    rows: [
      ['Browser requests per person', '10 per second, 300 per minute', 'The browser route, counted per person, or per client when no person is named', PLATFORM],
      ['Event names per workspace', '2,000 that are not ignored, 10,000 in all, ignored ones included', 'When an event name arrives for the first time', FIXED],
      ['New event names from a browser', '5 per request, and none while 100 wait to be organised', 'The browser route only', FIXED],
      ['Product events kept', '13 months from receipt', 'Storage', FIXED],
      ['Mapping suggestions per run', '15 events, and not the same event again within a week', 'Suggest from documentation', FIXED],
      ['Sites allowed to send from a browser', 'Only those listed in Settings, Product events, Browser tracking', 'The browser route', 'Workspace setting, by an admin'],
    ],
  },
  {
    title: 'Usage and event ingestion',
    rows: [
      ['Events per request', '1,000', 'POST /api/v1/events. More is refused with a 400', FIXED],
      ['Event timestamp, future', 'Not more than 15 minutes ahead of now', 'Per event. A later one is rejected', FIXED],
      ['Event timestamp, past', 'Not more than 7 days old', 'Per event. An older one is rejected as stale', FIXED],
      ['Duplicate event ids', 'Ignored for 24 hours in the fast check; a stored event also blocks a repeat id', 'Per workspace, per eventId', FIXED],
      ['Items per usage batch', '100', 'The usage batch endpoint, which also requires an Idempotency-Key header', FIXED],
      ['Usage history page', 'Default 100, at most 1,000 entries', 'The usage history endpoint', 'Per request, through the limit parameter'],
      ['Raw usage events kept', '90 days', 'Storage', FIXED],
      ['Seal delay for a closed period', '24 hours before a reading seals, checked hourly', 'The usage period sweep', PLATFORM],
    ],
  },
  {
    title: 'Outbound webhooks',
    intro:
      'Settings, Webhooks sends lead.created, lead.updated and lead.deleted to your web addresses. A webhook.test event is sent only by Send test event.',
    rows: [
      ['Endpoints per workspace', '25', 'When you add one', FIXED],
      ['Attempts per delivery', '7 in total, first included', 'The delivery sweep', FIXED],
      ['Wait before each retry', '30 seconds, 2 minutes, 10 minutes, 1 hour, 6 hours, 12 hours', 'The delivery sweep', FIXED],
      ['Answers that stop retrying', 'Any 4xx other than 408, 425 and 429, any redirect, and an address Revvly refuses to call. 5xx answers and network errors are retried', 'The delivery sweep', FIXED],
      ['Receiver Retry-After', 'Honoured up to 6 hours', 'The delivery sweep', FIXED],
      ['Switch-off after failures', '25 failed deliveries in a row turn the endpoint off', 'The endpoint', FIXED],
      ['Deliveries held for a switched-off endpoint', '7 days, then given up', 'The delivery sweep', FIXED],
      ['Pace', 'Checked every 5 seconds, at most 25 sends per endpoint per pass', 'The delivery sweep', FIXED],
    ],
  },
  {
    title: 'Forms',
    rows: [
      ['Field types', '16: Short text, Long text, Formatted text, Email, Phone, Website, Number, Date, Single choice, Multiple choice, Dropdown, Consent checkbox, Hidden value, File upload, Heading, Paragraph', 'The form builder', FIXED],
      ['Fields, actions, options', 'At most 100 fields, 20 actions, 100 options per field', 'The form builder', FIXED],
      ['Text lengths', 'Label 500 characters, description 2,000, email body 20,000', 'The form builder', FIXED],
      ['Honeypot', 'A text input named website. Any value is treated as a bot and answered with the normal success', 'The public submit route', FIXED],
      ['Submissions per form, public', '1,000 per UTC day', 'Link, embed and your own site', FIXED],
      ['Submissions per form, API', '10,000 per UTC day, a separate budget', 'The submitForm mutation and the API route', FIXED],
      ['Public form view', '120 per minute per visitor', 'GET /api/v1/forms/{publicId}', PLATFORM],
      ['Public submit and upload', '30 per minute per visitor', 'The submit and upload routes', PLATFORM],
      ['Upload slots per form', '3,000 per UTC day', 'The upload route', FIXED],
      ['Files per upload field', '1 to 5, at most 30 MB each', 'The form builder and the upload route', 'Per field, within those ceilings'],
      ['Upload link life', '15 minutes. An unclaimed upload is cleaned up after 24 hours', 'Storage', FIXED],
      ['Retries of one action step', '5 attempts', 'The form action runner', FIXED],
      ['Submission export', 'The newest 10,000 rows', 'Export to CSV', FIXED],
    ],
  },
  {
    title: 'Files and attachments',
    rows: [
      ['Document library file', '100 MB', 'Upload to Library', FIXED],
      ['Library file attached to an email', '10 MB. Larger files go as a link', 'The composer', FIXED],
      ['Library name, description, tags', '120 characters, 500 characters, 20 tags of 40 characters', 'Library', FIXED],
      ['Library list', 'The 500 most recently updated documents', 'Library', FIXED],
      ['Document links per email', '5', 'The composer', FIXED],
      ['Document link life', 'Default 180 days, at most 730', 'When you send', 'Per link, within the ceiling'],
      ['Email attachments', '5 files, 10 MB each, 20 MB in total', 'The composer and the server', FIXED],
      ['Email recipients', '50 each in To, Cc and Bcc', 'Send', FIXED],
      ['Support attachment', '20 MB per file, 10 files per message', 'Support replies and the chat upload', FIXED],
      ['Library upload link life', '1 hour. An unclaimed upload is cleaned up after 24 hours', 'Storage', FIXED],
    ],
  },
  {
    title: 'Email tracking',
    rows: [
      ['Open cooldown', '15 minutes per recipient: repeat loads inside it count as one open', 'Open tracking', FIXED],
      ['Document view cooldown', '15 minutes per link and viewer', 'Shared document links', FIXED],
      ['Opens shown per email', 'Up to 500', 'The email detail', FIXED],
      ['Open events kept', '1 year', 'Storage', FIXED],
      ['Document view events kept', '1 year', 'Storage', FIXED],
      ['Document view alerts', 'The sender is told about a link’s first 5 counted views', 'Shared document links', FIXED],
      ['Stored email body text', '24 months by default', 'Email storage', PLATFORM],
    ],
  },
  {
    title: 'Support',
    rows: [
      ['AI resolution list price', '0.35 USD per resolution', 'Billing of AI resolutions', PLATFORM],
      ['Assumed resolution', 'An AI answer the customer leaves unanswered for 24 hours, unless a person acted. Tickets older than 14 days are not back-filled', 'The 5-minute billing sweep', FIXED],
      ['Daily AI spend cap', 'No cap until you set one. 0 to 100,000 USD per day', 'Settings, Support, General', 'Workspace setting'],
      ['Support sandbox cap', '2 USD per day', 'The sandbox', FIXED],
      ['Auto-close', 'Waiting tickets close after 48 hours without a client reply by default. 1 to 720 hours, or off', 'Settings, Support, General', 'Workspace setting'],
      ['Email to clients who are away', 'Default 5 minutes unread. 1 to 1,440', 'Settings, Support, General', 'Workspace setting'],
      ['Knowledge articles per push', '50 per call. Deleting takes up to 200 externalIds per call', 'The knowledge API', FIXED],
      ['End-user token', 'Valid 60 seconds to 1 hour, 1 hour by default', 'The support sign-in mutation', 'Per request, through ttlSeconds, within the range'],
      ['Chat API, per person', '10 per second, 120 per minute', 'The Support chat API', PLATFORM],
      ['Chat API, per workspace', '200 per second, 6,000 per minute', 'The Support chat API', PLATFORM],
      ['Chat uploads, per person', '2 per second, 20 per minute, in addition to the chat limit', 'The Support chat upload route', PLATFORM],
      ['Chat query depth', '8 levels', 'The Support chat API', FIXED],
    ],
  },
  {
    title: 'Revvly Assistant',
    rows: [
      ['Proposal expiry', '15 minutes. An unanswered confirmation card expires and nothing runs', 'Confirmation cards', FIXED],
      ['Message length', '4,000 characters', 'Sending a message', FIXED],
      ['Attachments', '4 per message, 20 per conversation, 22 MB in total', 'Sending a message', FIXED],
      ['Per attachment', 'About 3.75 MB for an image, 20 MB for a PDF, 256 KB for text, 512 KB of text across a conversation', 'Sending a message', FIXED],
      ['Tool steps per reply', '8 rounds, then it answers in text', 'The assistant loop', FIXED],
      ['Model', 'claude-sonnet-5-5 unless the platform overrides it', 'The assistant', PLATFORM],
    ],
  },
  {
    title: 'Tasks and list sizes',
    rows: [
      ['Tasks list page', '50 tasks, then Load more', 'The Tasks page', FIXED],
      ['Work queue', '100 per page, up to 10 pages (1,000 tasks), then it says the list is incomplete', 'The Work queue', FIXED],
      ['Account tasks panel', 'The first 50 open tasks', 'The Work queue account column', FIXED],
      ['Client Tasks tab', 'The first 100 tasks', 'A client or company', FIXED],
      ['Task relation picker', '200 companies and 200 contacts at a time; search reaches the rest of the companies', 'New task', FIXED],
      ['Follow-up delay', 'Default 3 business days, 1 to 90', 'The composer, and Account, Email follow-up', 'Per person and per send'],
      ['Snooze', '1 to 365 days. Undo window 6 seconds', 'The Work queue', FIXED],
      ['Task reminders', 'Swept every minute. Nothing is sent to a person', 'The reminder job', PLATFORM],
      ['Repeating tasks', 'Next task created by a sweep every 30 minutes', 'The recurrence job', PLATFORM],
      ['Notification feed', 'Items kept 90 days', 'The bell', FIXED],
    ],
  },
  {
    title: 'Sessions and tokens',
    rows: [
      ['Dashboard access token', '15 minutes', 'Sign-in', PLATFORM],
      ['Sign-in session', '7 days', 'Sign-in', PLATFORM],
    ],
  },
  {
    title: 'Workspace defaults',
    intro: 'Where a setting has a screen, it is on Settings, General unless noted.',
    rows: [
      ['Default payment terms', '30 days, 1 to 365, or due on receipt', 'New invoices', 'Workspace setting; a client or invoice can override'],
      ['Churn grace period', '30 days, 1 to 365', 'Analytics', 'Workspace setting'],
      ['Reactivation window', '90 days, never below the churn grace period', 'Analytics', 'Stored per workspace; no screen yet'],
      ['Renewal invoice lead time', '0 days for monthly, quarterly and yearly, up to 365', 'Renewal invoicing', 'Workspace setting; a client can override'],
      ['Prepaid-term commitment', 'Binding', 'New prepaid-term schedules', 'Stored per workspace; no screen yet'],
      ['Deferred-activation gap', '60 days', 'The daily schedule sweep', 'Stored per workspace; no screen yet'],
      ['Working days', 'Monday to Friday, in the workspace timezone (UTC by default)', 'Business-day counts', 'Workspace setting, tenant admins only'],
      ['Booking intake daily limit', '100 companies per UTC day, at most 10,000', 'Settings, Booking intake', 'Workspace setting, tenant admins only'],
    ],
  },
  {
    title: 'Scheduled windows',
    intro: 'Daily jobs run on the platform clock. They are not workspace settings.',
    rows: [
      ['Overdue detection, daily snapshots', '1:00 and 2:00', 'Invoices and analytics', PLATFORM],
      ['Exchange rates', '2:00', 'Reporting currency conversion', PLATFORM],
      ['Renewals', '6:00', 'Renewal invoicing', PLATFORM],
      ['Usage periods closed', '4:00', 'Metered usage', PLATFORM],
      ['Payment reminders', '9:00', 'Skipped when Automatically send emails is off', PLATFORM],
      ['Support auto-close', 'Every 5 minutes', 'Support', PLATFORM],
    ],
  },
];

export function LimitsReference() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Limits and defaults — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Limits and defaults</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Limits and <em>defaults</em>.
        </h1>
        <p className="dp-oneliner">
          Every number you can hit, grouped by area: the value, where it is enforced, and whether
          you can change it. Each row was read from the product, not estimated.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">00</span>
          How to read the tables
        </h2>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Workspace setting</div>
            <div className="dp-field-v">An admin changes it in the dashboard.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Per key, per request</div>
            <div className="dp-field-v">You choose it on the key or the call, inside the ceiling shown.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Platform configuration</div>
            <div className="dp-field-v">Set by Revvly for the whole platform. A workspace admin cannot change it.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fixed</div>
            <div className="dp-field-v">Part of the product. It changes only with a release.</div>
          </div>
        </div>
      </section>

      {AREAS.map((area, i) => (
        <section className="dp-section" key={area.title}>
          <h2>
            <span className="dp-step-num">{String(i + 1).padStart(2, '0')}</span>
            {area.title}
          </h2>
          {area.intro ? <p className="dp-plain">{area.intro}</p> : null}
          <div className="dp-fields-table" style={{ marginTop: 18 }}>
            {area.rows.map(([item, value, where, change]) => (
              <div className="dp-field-row" key={item}>
                <div className="dp-field-k">{item}</div>
                <div className="dp-field-v">
                  <strong>{value}</strong>
                  <br />
                  Enforced: {where}. Change: {change}.
                </div>
              </div>
            ))}
          </div>
          {i === 0 ? (
            <>
              <p className="dp-plain" style={{ marginTop: 18 }}>
                Over the limit, REST routes answer with a 429 and a Retry-After header in seconds.
                The public GraphQL API answers with the same status and the code RATE_LIMITED.
              </p>
              <pre className="api-code">{REST_429}</pre>
              <pre className="api-code">{GQL_429}</pre>
              <p className="dp-plain" style={{ marginTop: 18 }}>
                Wait for the Retry-After value, then retry with the same idempotency key. See{' '}
                <Link to="/docs/guides/api-keys">API keys for integrators</Link> and{' '}
                <Link to="/docs/api/events">Event ingestion</Link>.
              </p>
            </>
          ) : null}
        </section>
      ))}
    </div>
  );
}
